// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { ChevronUpIcon, LayerGroupIcon } from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipArrow,
} from '@frontend/applications/Social/components/tooltip';
import type { GetStageTemplates_getStageTemplates as IStageTemplate } from '@frontend/app/queries/types/GetStageTemplates';
import { useCustomStages } from '../context/CustomStagesContext';

// Import existing components
import { FormFieldsSection } from './FormFieldsSection';
import { TaskSettingsSection } from './TaskSettingsSection';
import { StatusSection } from './StatusSection';
import { TemplatesDrawer } from './TemplatesDrawer';

interface Props {
  tasks: Array<{
    name: string;
    ctaLabel: string;
    creatorAccess: boolean;
    fields: Array<{
      id: string;
      label: string;
      type: string;
      required: boolean;
      options?: string[];
      description?: string;
      campaignOnly?: boolean;
    }>;
  }>;
  setTasks: (tasks: Array<any>) => void;
  taskSettings?: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  };
  setTaskSettings: (settings: any) => void;
}

export const SettingsSection: React.FC<Props> = ({
  tasks,
  setTasks,
  taskSettings,
  setTaskSettings
}) => {
  const {
    selectedStage,
    selectedStageId,
    setHasUnsavedChanges,
    updateTaskSettings,
    updateCreatorAccess,
    applyTemplate,
  } = useCustomStages();
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(true);
  const [isStatusesExpanded, setIsStatusesExpanded] = useState(true);
  const [isFieldsExpanded, setIsFieldsExpanded] = useState(true);
  const [isCreatorInteractionExpanded, setIsCreatorInteractionExpanded] = useState(true);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);

  // Local state for form fields
  const [localFields, setLocalFields] = useState(tasks[0]?.fields || []);
  const initialFieldsRef = useRef<any[]>([]);
  const stageKeyRef = useRef<string | null>(null);

  // Initialize local state and refs when stage changes
  useEffect(() => {
    const currentStageKey = `${selectedStage?.id || 'new'}`;
    if (stageKeyRef.current !== currentStageKey) {
      stageKeyRef.current = currentStageKey;
      const fields = tasks[0]?.fields || [];
      setLocalFields(fields);
      initialFieldsRef.current = JSON.parse(JSON.stringify(fields));
    }
  }, [selectedStage?.id, tasks]);

  const handleFieldsChange = (fields: any[]) => {
    setLocalFields(fields);
    const updatedTask = { ...tasks[0], fields };
    setTasks([updatedTask]);

    // Compare with initial fields to detect changes
    const hasFieldChanges = JSON.stringify(fields) !== JSON.stringify(initialFieldsRef.current);
    setHasUnsavedChanges(hasFieldChanges);
  };

  const handleCreatorAccessChange = (creatorAccess: boolean) => {
    if (selectedStage?.id) {
      updateCreatorAccess(selectedStage.id, creatorAccess);
    }
  };

  const handleTaskSettingsChange = (settings: {
    initialStatuses: Array<{ id: number; name: string }>;
    finalStatus: { id: number; name: string } | null;
  }) => {
    if (selectedStage?.id) {
      updateTaskSettings(selectedStage.id, settings);
    }
  };

  const handleTemplateSelect = (template: IStageTemplate) => {
    if (selectedStage?.id) {
      applyTemplate(selectedStage.id, {
        name: template.name,
        description: template.description,
        statuses: template.statuses.map((status) => ({
          name: status.name,
          bgColor: status.bgColor,
        })),
      });
    }
  };

  return (
    <div className="w-full p-4 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="flex-1 text-gray-800 text-base font-medium leading-7">Settings</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="h-6 rounded-lg flex items-center gap-2 hover:bg-gray-50 px-2"
                onClick={() => setIsTemplatesOpen(true)}
              >
                <div className="w-3.5 h-3.5 flex justify-center items-center">
                  <LayerGroupIcon className="text-gray-800" />
                </div>
                <span className="text-gray-800 text-sm font-medium leading-normal">Templates</span>
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={8}
              className="bg-[#1C2024] text-white text-sm px-2 py-1.5 border-0 rounded-lg shadow-[0px_2px_6px_-4px_rgba(0,0,0,0.12),0px_4px_8px_0px_rgba(93,105,116,0.08),0px_8px_24px_8px_rgba(93,105,116,0.05)]"
            >
              Choose a preset template for your custom stage.
              <TooltipArrow style={{ fill: '#1C2024' }} className="h-1.5" />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <button
          className="w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center"
          onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
        >
          <ChevronUpIcon
            className={cn('w-3.5 h-3.5 text-gray-800 transition-transform',
            !isSettingsExpanded && 'rotate-180')}
          />
        </button>
      </div>

      {/* Divider */}
      <div className="h-0.5 relative overflow-hidden">
        <div className="w-full h-0 absolute outline outline-1 outline-offset-[-0.50px] outline-gray-200" />
      </div>

      {isSettingsExpanded && (
        <div className="flex flex-col gap-4">
          {/* Statuses Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Statuses</span>
              <button
                className="w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center"
                onClick={() => setIsStatusesExpanded(!isStatusesExpanded)}
              >
                <ChevronUpIcon
                  className={cn('w-3.5 h-3.5 text-gray-800 transition-transform',
                  !isStatusesExpanded && 'rotate-180')}
                />
              </button>
            </div>
            {isStatusesExpanded && <StatusSection />}
          </div>

          {/* Divider */}
          <div className="h-0.5 relative overflow-hidden">
            <div className="w-full h-0 absolute outline outline-1 outline-offset-[-0.50px] outline-gray-200" />
          </div>

          {/* Fields Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Fields</span>
              <button
                className="w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center"
                onClick={() => setIsFieldsExpanded(!isFieldsExpanded)}
              >
                <ChevronUpIcon
                  className={cn('w-3.5 h-3.5 text-gray-800 transition-transform',
                  !isFieldsExpanded && 'rotate-180')}
                />
              </button>
            </div>
            {isFieldsExpanded && (
              <FormFieldsSection
                fields={localFields}
                onFieldsChange={handleFieldsChange}
              />
            )}
          </div>

          {/* Divider */}
          <div className="h-0.5 relative overflow-hidden">
            <div className="w-full h-0 absolute outline outline-1 outline-offset-[-0.50px] outline-gray-200" />
          </div>

          {/* Creator Interaction Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Creator Interaction</span>
              <button
                className="w-8 h-8 px-2 py-1 rounded-lg flex justify-center items-center"
                onClick={() => setIsCreatorInteractionExpanded(!isCreatorInteractionExpanded)}
              >
                <ChevronUpIcon
                  className={cn('w-3.5 h-3.5 text-gray-800 transition-transform',
                  !isCreatorInteractionExpanded && 'rotate-180')}
                />
              </button>
            </div>
            {isCreatorInteractionExpanded && (
              <TaskSettingsSection
                creatorAccess={tasks[0]?.creatorAccess || false}
                onCreatorAccessChange={handleCreatorAccessChange}
                statuses={selectedStage?.statuses || []}
                selectedTaskStatuses={selectedStage?.taskSettings?.initialStatuses || []}
                selectedResultingStatus={selectedStage?.taskSettings?.finalStatus || null}
                onTaskSettingsChange={handleTaskSettingsChange}
              />
            )}
          </div>
        </div>
      )}

      <TemplatesDrawer
        open={isTemplatesOpen}
        onOpenChange={setIsTemplatesOpen}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
};
